import React, { useEffect, useState, useRef } from "react";
import WebService from "../Services/WebService";
import styles from "./AddRawMaterialPanel.module.css";
import AddCategoryPopUp from "./AddCategoryPopup";
import CrossButton from "../Customs/Button/CrossButton";

const DropdownWithSearch = ({
  label,
  value,
  onChange,
  searchValue,
  setSearchValue,
  options,
  placeholder,
  error,
  example,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  searchValue: string;
  setSearchValue: (v: string) => void;
  options: { value: string | number; label: string }[];
  placeholder: string;
  error?: string;
  example?: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (!open) setOpen(true);
  };

  return (
    <div className={styles.dropdownWrapper} ref={ref}>
      <div
        className={`${styles.inputBox} ${error ? styles.inputError : ""}`}
        onClick={() => setOpen(true)}
        tabIndex={0}
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <span>
          {options.find((o) => String(o.value) === value)?.label || placeholder}
          {example && (
            <span className={styles.prefixExample} style={{ float: "right" }}>{example}</span>
          )}
        </span>
        <span className={styles.dropdownArrow} style={{ marginLeft: "auto" }}></span>
      </div>
      {open && (
        <div className={styles.dropdownMenu} onMouseDown={e => e.stopPropagation()}>
          <input
            type="text"
            className={styles.dropdownSearch}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder={"Search " + label}
            autoFocus
            style={{ marginBottom: 7 }}
            onClick={handleInputClick}
          />
          {options.length > 0 ? (
            options.map(o => (
              <div
                key={o.value}
                className={styles.dropdownOption}
                onClick={() => {
                  onChange(String(o.value));
                  setOpen(false);
                }}
              >
                {o.label}
              </div>
            ))
          ) : (
            <div className={styles.dropdownNoOption}>No options found</div>
          )}
        </div>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

interface RawMaterial {
  id: number;
  sku_id: string;
  name: string;
  measuring_unit: string;
  measuring_unit_value: number;
  quantity: number;
  min_stock_qty?: number;
  min_po_qty?: number;
  default_vendor_id?: number;
  category: string;
  is_trading_allowed: boolean;
  is_return_allowed: boolean;
  cost_price: number;
  selling_price: number;
  distributor_selling_price: number;
  retailer_selling_price: number;
  gst_percentage: number;
  hsn_code?: string | null; // ✅ added
  description: string;
  images?: any[];
}

interface Vendor {
  user_id: number;
  vendor_id: number;
  username: string;
  company_name: string;
  mobile_number: string;
}

interface PrefixOption { prefix_id: number; name: string; prefix: string; }
interface MeasuringUnit { measuring_unit_id: number; name: string; symbol: string; }
interface CategoryOption { category_id: number; category_name: string; }
interface ValidationErrors { [key: string]: string; }

interface FormDataState {
  name: string;
  prefix_id: string;
  measuring_unit_id: string;
  measuring_unit_value: string;
  category_id: string;
  cost_price: string;
  selling_price: string;
  quantity: string;
  min_stock_qty: string;
  min_po_qty: string;
  default_vendor_id: string;
  is_trading_allowed: boolean;
  is_return_allowed: boolean;
  distributor_selling_price: string;
  retailer_selling_price: string;
  gst_percentage: string;
  hsn_code: string; 
  description: string;
}

const initialFormState: FormDataState = {
  name: "",
  prefix_id: "",
  measuring_unit_id: "",
  measuring_unit_value: "",
  category_id: "",
  cost_price: "",
  selling_price: "",
  quantity: "",
  min_stock_qty: "",
  min_po_qty: "",
  default_vendor_id: "",
  is_trading_allowed: true,
  is_return_allowed: true,
  distributor_selling_price: "",
  retailer_selling_price: "",
  gst_percentage: "18",
  hsn_code: "", // ✅ added
  description: "",
};

interface AddRawMaterialPanelProps {
  onClose: (refresh?: boolean) => void;
  editMaterial?: RawMaterial | null;
  showToast?: (msg: string, type: "success" | "error") => void;
  canWrite?: boolean;
  canUpdate?: boolean;
}

const AddRawMaterialPanel: React.FC<AddRawMaterialPanelProps> = ({
  onClose,
  editMaterial = null,
  showToast,
  canWrite = false,
  canUpdate = false,
}) => {
  const [prefixes, setPrefixes] = useState<PrefixOption[]>([]);
  const [units, setUnits] = useState<MeasuringUnit[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [prefixSearch, setPrefixSearch] = useState("");
  const [unitSearch, setUnitSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorHasMore, setVendorHasMore] = useState(true);
  const [formData, setFormData] = useState<FormDataState>({ ...initialFormState });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const minStockQtyInputRef = useRef<HTMLInputElement>(null);

  // Local toast fallback if not passed from parent
  const [localToast, setLocalToast] = useState<{ message: string; type: "success" | "error" | "" }>({ message: "", type: "" });
  const triggerToast = (msg: string, type: "success" | "error") => {
    if (showToast) {
      showToast(msg, type);
    } else {
      setLocalToast({ message: msg, type });
      setTimeout(() => setLocalToast({ message: "", type: "" }), 2100);
    }
  };

  useEffect(() => {
    if (editMaterial && categories.length && prefixes.length && units.length) {
      const matchedCategory = categories.find(
        c => c.category_name.toLowerCase() === String(editMaterial.category).toLowerCase()
      );
      const matchedPrefix = prefixes.find(
        p =>
          p.prefix.toLowerCase() === String(editMaterial.sku_id).split("-")[0].toLowerCase() ||
          String(editMaterial.sku_id).toLowerCase().startsWith(p.prefix.toLowerCase())
      );
      const matchedUnit = units.find(
        u => u.name.toLowerCase() === String(editMaterial.measuring_unit).toLowerCase()
      );

      const cleanHSN =
        editMaterial.hsn_code && editMaterial.hsn_code.toString().trim().toLowerCase() !== "null"
          ? String(editMaterial.hsn_code)
          : ""; // ✅ blank when not present

      setFormData({
        name: editMaterial.name || "",
        prefix_id: matchedPrefix ? String(matchedPrefix.prefix_id) : "",
        measuring_unit_id: matchedUnit ? String(matchedUnit.measuring_unit_id) : "",
        measuring_unit_value: editMaterial.measuring_unit_value ? String(editMaterial.measuring_unit_value) : "",
        category_id: matchedCategory ? String(matchedCategory.category_id) : "",
        cost_price: editMaterial.cost_price ? String(editMaterial.cost_price) : "",
        selling_price: editMaterial.selling_price ? String(editMaterial.selling_price) : "",
        quantity: editMaterial.quantity ? String(editMaterial.quantity) : "",
        min_stock_qty: editMaterial.min_stock_qty ? String(editMaterial.min_stock_qty) : "",
        min_po_qty: editMaterial.min_po_qty ? String(editMaterial.min_po_qty) : "",
        default_vendor_id: editMaterial.default_vendor_id ? String(editMaterial.default_vendor_id) : "",
        is_trading_allowed: typeof editMaterial.is_trading_allowed === "boolean"
          ? editMaterial.is_trading_allowed
          : Boolean(editMaterial.is_trading_allowed),
        is_return_allowed: typeof editMaterial.is_return_allowed === "boolean"
          ? editMaterial.is_return_allowed
          : Boolean(editMaterial.is_return_allowed),
        distributor_selling_price: editMaterial.distributor_selling_price ? String(editMaterial.distributor_selling_price) : "",
        retailer_selling_price: editMaterial.retailer_selling_price ? String(editMaterial.retailer_selling_price) : "",
        gst_percentage: editMaterial.gst_percentage ? String(editMaterial.gst_percentage) : "18",
        hsn_code: cleanHSN, 
        description: editMaterial.description || "",
      });
    } else if (!editMaterial) {
      setFormData({ ...initialFormState });
    }
  }, [editMaterial, categories, prefixes, units]);

  useEffect(() => { fetchDropdowns(); fetchVendors(); }, []);

  const fetchVendors = async (searchQuery: string = "", page: number = 1) => {
    setVendorLoading(true);
    try {
      const query = [
        `limit=10`,
        `page_number=${page}`,
        searchQuery ? `name=${encodeURIComponent(searchQuery)}` : "",
      ].filter(Boolean).join("&");
      
      const res: any = await WebService.getAPI({
        action: `/v1/door-management/vendors?${query}`,
      });
      
      const newVendors = res?.data?.vendors || [];
      const pagination = res?.data?.pagination || {};
      
      if (page === 1) {
        setVendors(newVendors);
      } else {
        setVendors(prev => [...prev, ...newVendors]);
      }
      
      setVendorPage(page);
      setVendorHasMore(page < (pagination.total_pages || 1));
    } catch {
      if (page === 1) setVendors([]);
      setVendorHasMore(false);
    } finally {
      setVendorLoading(false);
    }
  };

  const handleVendorSearch = (query: string) => {
    setVendorSearch(query);
    setVendorPage(1);
    fetchVendors(query, 1);
  };

  const handleVendorLoadMore = () => {
    if (!vendorLoading && vendorHasMore) {
      fetchVendors(vendorSearch, vendorPage + 1);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [prefixRes, unitRes, catRes] = await Promise.all([
        WebService.getAPI<{ data: PrefixOption[] }>({ action: "/v1/door-management/prefix" }),
        WebService.getAPI<{ data: MeasuringUnit[] }>({ action: "/v1/door-management/measuring-units" }),
        WebService.getAPI<{ data: CategoryOption[] }>({ action: "/v1/door-management/raw-material/categories" }),
      ]);
      setPrefixes(Array.isArray(prefixRes?.data) ? prefixRes.data : []);
      setUnits(Array.isArray(unitRes?.data) ? unitRes.data : []);
      const catRaw = Array.isArray(catRes?.data)
        ? catRes.data
        : Array.isArray((catRes as any)?.data?.data)
        ? (catRes as any).data.data
        : [];
      setCategories(catRaw.filter((c: CategoryOption) => c != null && c.category_id != null));
    } catch {}
  };

  // Prevent wheel scrolling on min stock qty number input
  useEffect(() => {
    const input = minStockQtyInputRef.current;
    if (!input) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
    };

    input.addEventListener("wheel", handleWheel, { passive: false });
    return () => input.removeEventListener("wheel", handleWheel);
  }, []);

  // --- VALIDATION (MANDATORY LOGIC FULL) ---
  const validate = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};
    if (!formData.name.trim()) newErrors.name = "Item Name is required.";

    if (!formData.quantity.trim()) newErrors.quantity = "Quantity is required.";
    else if (isNaN(Number(formData.quantity)) || Number(formData.quantity) <= 0)
      newErrors.quantity = "Quantity must be a positive number.";

    if (!formData.prefix_id) newErrors.prefix_id = "SKU Prefix is required.";
    if (!formData.category_id) newErrors.category_id = "Category is required.";
    if (!formData.measuring_unit_id) newErrors.measuring_unit_id = "Measuring Unit is required.";

    if (!formData.measuring_unit_value.trim()) newErrors.measuring_unit_value = "Measuring value is required.";
    else if (isNaN(Number(formData.measuring_unit_value)) || Number(formData.measuring_unit_value) <= 0)
      newErrors.measuring_unit_value = "Measuring value must be a positive number.";

    if (!formData.selling_price.trim()) newErrors.selling_price = "Selling price is required.";
    else if (isNaN(Number(formData.selling_price)) || Number(formData.selling_price) < 0)
      newErrors.selling_price = "Selling price must be positive.";

    if (!formData.cost_price.trim()) newErrors.cost_price = "Cost price is required.";
    else if (isNaN(Number(formData.cost_price)) || Number(formData.cost_price) < 0)
      newErrors.cost_price = "Cost price must be positive.";

    if (!formData.distributor_selling_price.trim()) newErrors.distributor_selling_price = "Distributor price is required.";
    else if (isNaN(Number(formData.distributor_selling_price)) || Number(formData.distributor_selling_price) < 0)
      newErrors.distributor_selling_price = "Distributor price must be positive.";

    if (!formData.retailer_selling_price.trim()) newErrors.retailer_selling_price = "Retailer price is required.";
    else if (isNaN(Number(formData.retailer_selling_price)) || Number(formData.retailer_selling_price) < 0)
      newErrors.retailer_selling_price = "Retailer price must be positive.";

    if (formData.gst_percentage) {
      const val = Number(formData.gst_percentage);
      if (isNaN(val) || val < 0 || val > 100)
        newErrors.gst_percentage = "GST % must be between 0 and 100.";
    }

    // HSN is optional — no hard validation
    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === "checkbox" && (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleToggle = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: !prev[name as keyof typeof prev],
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    setLoading(true);

    const payload = {
      ...formData,
      name: formData.name.trim(),
      prefix_id: Number(formData.prefix_id),
      measuring_unit_id: Number(formData.measuring_unit_id),
      measuring_unit_value: parseFloat(formData.measuring_unit_value ?? "0"),
      category_id: Number(formData.category_id),
      cost_price: parseFloat(formData.cost_price ?? "0") || 0,
      selling_price: parseFloat(formData.selling_price ?? "0") || 0,
      quantity: parseFloat(formData.quantity ?? "0") || 0,
      min_stock_qty: parseFloat(formData.min_stock_qty ?? "0") || 0,
      min_po_qty: parseFloat(formData.min_po_qty ?? "0") || 0,
      default_vendor_id: formData.default_vendor_id ? Number(formData.default_vendor_id) : null,
      distributor_selling_price: parseFloat(formData.distributor_selling_price ?? "0") || 0,
      retailer_selling_price: parseFloat(formData.retailer_selling_price ?? "0") || 0,
      gst_percentage: parseFloat(formData.gst_percentage ?? "0") || 0,
      hsn_code: formData.hsn_code.trim(), // ✅ include in payload (blank if not provided)
      description: formData.description.trim(),
    };

    try {
      let res: any;
      if (editMaterial) {
        const editPayload = { ...payload, id: editMaterial.id };
        res = await WebService.putAPI({
          action: `/v1/door-management/raw-material`,
          body: editPayload,
        });
      } else {
        res = await WebService.postAPI({
          action: "/v1/door-management/raw-material",
          body: payload,
        });
      }

      if (res?.status_code === 200 || res?.status_code === 201) {
        const rawMaterialId =
          editMaterial?.id ||
          res?.data?.raw_material_id ||
          res?.data?.id ||
          res?.data?.data?.raw_material_id ||
          res?.data?.data?.id;

        if (file && rawMaterialId) {
          try {
            await WebService.fileUploadAPI({
              action: `/v1/door-management/raw-material/images/${rawMaterialId}`,
              file,
              key: "file",
              body: {},
            });
          } catch {
            triggerToast(
              editMaterial
                ? "Material updated, but image upload failed."
                : "Raw material added, but image upload failed.",
              "error"
            );
            setLoading(false);
            setTimeout(() => {
              setFormData({ ...initialFormState });
              setFile(null);
              setErrors({});
              onClose(true);
            }, 1100);
            return;
          }
        }

        triggerToast(
          editMaterial ? "Material updated successfully!" : "Raw material added successfully!",
          "success"
        );
        setTimeout(() => {
          setFormData({ ...initialFormState });
          setFile(null);
          setErrors({});
          onClose(true);
        }, 1100);
      } else {
        triggerToast(res?.message || "Failed to save material.", "error");
      }
    } catch (error: any) {
      triggerToast(error?.response?.data?.message || error?.message || "Failed to save material.", "error");
    } finally {
      setLoading(false);
    }
  };

  const ToggleSwitch: React.FC<{
    checked: boolean;
    onChange: () => void;
    label: string;
    id: string;
  }> = ({ checked, onChange, label, id }) => (
    <div className={styles.toggleSwitchRow}>
      <label htmlFor={id} className={styles.toggleSwitchLabel} style={{ cursor: "pointer" }}>
        {label}
      </label>
      <div className={styles.toggleSwitchBox}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className={styles.toggleSwitchInput}
        />
        <span className={styles.toggleSwitchSlider}>
          <span className={styles.toggleSwitchKnob} />
        </span>
      </div>
    </div>
  );

  const filteredPrefixes = prefixes.filter(
    p =>
      p.prefix.toLowerCase().includes(prefixSearch.toLowerCase()) ||
      p.name.toLowerCase().includes(prefixSearch.toLowerCase())
  );
  const filteredUnits = units.filter(
    u =>
      u.name.toLowerCase().includes(unitSearch.toLowerCase()) ||
      u.symbol.toLowerCase().includes(unitSearch.toLowerCase())
  );
  const filteredCategories = categories.filter(
    c => (c.category_name || "").toLowerCase().includes(categorySearch.toLowerCase())
  );
  const selectedPrefix = prefixes.find(p => String(p.prefix_id) === formData.prefix_id);
  const prefixExample = selectedPrefix ? `${selectedPrefix.prefix}-001` : "";

  // MAIN RETURN
  return (
    <div className={styles.addPanelOverlay} onClick={() => onClose(false)}>
      <div className={styles.addPanelBox} onClick={e => e.stopPropagation()}>
        <CrossButton onClick={() => onClose(false)} />

        {localToast.message && (
          <div className={styles.toastMsg}>
            {localToast.type === "success" ? "✔️" : "❌"}&nbsp;{localToast.message}
          </div>
        )}
        <h2 className={styles.heading}>{editMaterial ? "Edit Raw Material" : "Add New Raw Material"}</h2>
        <div className={styles.subheading}>Add door raw materials to your inventory</div>
        <form
          className={styles.formWrap}
          autoComplete="off"
          onSubmit={e => { e.preventDefault(); handleSubmit(); }}
        >
          {/* 1ST ROW */}
          <div className={styles.grid2col}>
            {/* Name */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Item Name*</label>
              <input
                name="name"
                className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
                placeholder="Enter Item Name"
                value={formData.name}
                onChange={handleChange}
                autoFocus
              />
              {errors.name && <div className={styles.error}>{errors.name}</div>}
            </div>
            {/* Quantity */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Add Quantity*</label>
              <input
                name="quantity"
                className={`${styles.input} ${errors.quantity ? styles.inputError : ""}`}
                placeholder="0"
                value={formData.quantity}
                onChange={handleChange}
                disabled={!!editMaterial}
                style={!!editMaterial ? { background: "#f9f9f9", color: "#888" } : {}}
              />
              {errors.quantity && <div className={styles.error}>{errors.quantity}</div>}
              {editMaterial && (
                <div style={{ color: "#b97e09", fontSize: 12, marginTop: 3 }}>
                  Quantity can't be changed here. Use "Adjust Stock" on the detail page.
                </div>
              )}
            </div>
            {/* Min Stock Quantity */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Min Stock Quantity</label>
              <input
                ref={minStockQtyInputRef}
                name="min_stock_qty"
                type="number"
                className={styles.input}
                placeholder="0"
                value={formData.min_stock_qty}
                onChange={handleChange}
                min="0"
                step="1"
              />
            </div>
            {/* Min Purchase Order Qty */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Min Purchase Order Qty</label>
              <input
                name="min_po_qty"
                type="number"
                className={styles.input}
                placeholder="0"
                value={formData.min_po_qty}
                onChange={handleChange}
                min="0"
                step="1"
              />
            </div>
            {/* Default Vendor */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Default Vendor</label>
              <div className={styles.vendorContainer}>
                <input
                  type="text"
                  className={styles.vendorSearchInput}
                  placeholder={formData.default_vendor_id ? "Click to change vendor..." : "Search vendor..."}
                  value={vendorSearch}
                  onChange={e => handleVendorSearch(e.target.value)}
                  onFocus={() => formData.default_vendor_id && setVendorSearch("")}
                  autoComplete="off"
                />
                {vendorSearch && (
                  <div className={styles.vendorDropdownMenu}>
                    {vendorLoading && (
                      <div className={styles.vendorLoading}>Loading vendors...</div>
                    )}
                    {!vendorLoading && vendors.length === 0 && (
                      <div className={styles.vendorEmpty}>No vendors found</div>
                    )}
                    {vendors.map(vendor => (
                      <div
                        key={vendor.vendor_id}
                        className={`${styles.vendorDropdownItem} ${
                          formData.default_vendor_id === String(vendor.vendor_id)
                            ? styles.selected
                            : ""
                        }`}
                        onClick={() => {
                          setFormData(f => ({
                            ...f,
                            default_vendor_id: String(vendor.vendor_id),
                          }));
                          setVendorSearch("");
                        }}
                      >
                        <span>
                          {vendor.company_name}
                          {vendor.username && ` (${vendor.username})`}
                        </span>
                      </div>
                    ))}
                    {vendorHasMore && !vendorLoading && (
                      <div
                        className={styles.vendorLoadMore}
                        onClick={handleVendorLoadMore}
                      >
                        Load More Vendors
                      </div>
                    )}
                  </div>
                )}
                {formData.default_vendor_id && !vendorSearch && (
                  <div className={styles.vendorSelected}>
                    <div className={styles.vendorChip}>
                      {vendors.find(v => String(v.vendor_id) === formData.default_vendor_id)
                        ?.company_name || "Vendor"}
                    </div>
                    <button
                      type="button"
                      className={styles.vendorClearBtn}
                      onClick={() => {
                        setFormData(f => ({ ...f, default_vendor_id: "" }));
                        setVendorSearch("");
                      }}
                      title="Remove vendor"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* Returnable Toggle */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Returnable Item</label>
              <div className={styles.toggleSwitchRow}>
                <ToggleSwitch
                  checked={formData.is_return_allowed}
                  onChange={() => handleToggle("is_return_allowed")}
                  label="Enable returns"
                  id="is_return_allowed"
                />
              </div>
            </div>
            {/* SKU Dropdown */}
            <div className={styles.formGroup}>
              <label className={styles.label}>SKU ID*</label>
              <DropdownWithSearch
                label="SKU ID"
                value={formData.prefix_id}
                onChange={val => setFormData(f => ({ ...f, prefix_id: val }))}
                searchValue={prefixSearch}
                setSearchValue={setPrefixSearch}
                options={filteredPrefixes.map(p => ({
                  value: p.prefix_id, label: `${p.prefix} - ${p.name}`,
                }))}
                placeholder="Select prefix *"
                error={errors.prefix_id}
                example={prefixExample}
              />
            </div>
          </div>

          {/* Category Row */}
          <div className={styles.categoryRow}>
            <div className={styles.formGroup} style={{ flex: 1 }}>
              <label className={styles.label}>Choose Categories</label>
              <DropdownWithSearch
                label="Categories"
                value={formData.category_id}
                onChange={val => setFormData(f => ({ ...f, category_id: val }))}
                searchValue={categorySearch}
                setSearchValue={setCategorySearch}
                options={filteredCategories.map(c => ({
                  value: c.category_id, label: c.category_name,
                }))}
                placeholder="Choose Category *"
                error={errors.category_id}
              />
            </div>
            <button
              type="button"
              className={styles.addCategoryBtn}
              onClick={() => setShowAddCategory(true)}
            >Add New</button>
          </div>

          {/* 2nd ROW */}
          <div className={styles.grid2col}>
            {/* HSN Code (optional) */}
            <div className={styles.formGroup}>
              <label className={styles.label}>HSN Code</label>
              <input
                name="hsn_code"
                className={styles.input}
                placeholder="e.g., 4412"
                value={formData.hsn_code}
                onChange={handleChange}
              />
            </div>

            {/* Measuring Unit */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Measuring Unit*</label>
              <DropdownWithSearch
                label="Measuring Unit"
                value={formData.measuring_unit_id}
                onChange={val => setFormData(f => ({ ...f, measuring_unit_id: val }))}
                searchValue={unitSearch}
                setSearchValue={setUnitSearch}
                options={filteredUnits.map(u => ({
                  value: u.measuring_unit_id, label: `${u.name} (${u.symbol})`,
                }))}
                placeholder="Select Unit *"
                error={errors.measuring_unit_id}
              />
            </div>

            {/* Measuring Value */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Measuring Value*</label>
              <input
                name="measuring_unit_value"
                className={`${styles.input} ${errors.measuring_unit_value ? styles.inputError : ""}`}
                placeholder="1 pcs = ?"
                value={formData.measuring_unit_value}
                onChange={handleChange}
              />
              {errors.measuring_unit_value && <div className={styles.error}>{errors.measuring_unit_value}</div>}
            </div>

            {/* Trading Toggle */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Enable trading</label>
              <div className={styles.toggleSwitchRow}>
                <ToggleSwitch
                  checked={formData.is_trading_allowed}
                  onChange={() => handleToggle("is_trading_allowed")}
                  label="Enable Order for trading"
                  id="is_trading_allowed"
                />
              </div>
            </div>
          </div>

          <div className={styles.sectionDivider} />

          {/* SALES SECTION */}
          <div className={styles.sectionLabel}>Sales Information</div>
          <div className={styles.grid2col}>
            {/* Selling Price */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Selling Price (INR)</label>
              <input
                name="selling_price"
                className={`${styles.input} ${errors.selling_price ? styles.inputError : ""}`}
                placeholder="₹ 0.00"
                value={formData.selling_price}
                onChange={handleChange}
              />
              {errors.selling_price && <div className={styles.error}>{errors.selling_price}</div>}
            </div>
            {/* Cost Price */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Set Cost Price (INR)</label>
              <input
                name="cost_price"
                className={`${styles.input} ${errors.cost_price ? styles.inputError : ""}`}
                placeholder="₹ 0.00"
                value={formData.cost_price}
                onChange={handleChange}
              />
              {errors.cost_price && <div className={styles.error}>{errors.cost_price}</div>}
            </div>
            {/* Distributor Price */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Distributor Customer Selling Price (INR)</label>
              <input
                name="distributor_selling_price"
                className={`${styles.input} ${errors.distributor_selling_price ? styles.inputError : ""}`}
                placeholder="₹ 0.00"
                value={formData.distributor_selling_price}
                onChange={handleChange}
              />
              {errors.distributor_selling_price && <div className={styles.error}>{errors.distributor_selling_price}</div>}
            </div>
            {/* Retailer Price */}
            <div className={styles.formGroup}>
              <label className={styles.label}>Retailer Customer Selling Price (INR)</label>
              <input
                name="retailer_selling_price"
                className={`${styles.input} ${errors.retailer_selling_price ? styles.inputError : ""}`}
                placeholder="₹ 0.00"
                value={formData.retailer_selling_price}
                onChange={handleChange}
              />
              {errors.retailer_selling_price && <div className={styles.error}>{errors.retailer_selling_price}</div>}
            </div>
            {/* GST */}
            <div className={styles.formGroup}>
              <label className={styles.label}>GST percentage</label>
              <input
                name="gst_percentage"
                className={`${styles.input} ${errors.gst_percentage ? styles.inputError : ""}`}
                placeholder="% 18"
                value={formData.gst_percentage}
                onChange={handleChange}
              />
              {errors.gst_percentage && <div className={styles.error}>{errors.gst_percentage}</div>}
            </div>
          </div>

          {/* Description */}
          <div className={styles.formGroup} style={{ marginTop: 12 }}>
            <label className={styles.label}>Add Description</label>
            <textarea
              name="description"
              className={styles.textarea}
              placeholder="Enter item description..."
              value={formData.description}
              onChange={handleChange}
              rows={3}
              style={{ minHeight: 56 }}
            />
          </div>

          {/* Upload Images */}
          <div className={styles.sectionLabel}>Upload Product Images</div>
          <div className={styles.uploadBox}>
            <input
              type="file"
              accept=".png,.jpg,.jpeg"
              id="file-upload"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload" className={styles.uploadLabel}>
              Drag and drop images here, or <span className={styles.browse}>browse files</span>
            </label>
            <div className={styles.uploadHint}>PNG, .JPG up to 5MB each</div>
            {file && <div style={{ fontSize: 14, color: "#474747", marginTop: 3 }}>{file.name}</div>}
          </div>
        </form>

        <div className={styles.footerActions}>
          <button type="button" onClick={() => onClose(false)} disabled={loading}>Cancel</button>
          <button
            type="button"
            onClick={() => {
              if (editMaterial && !canUpdate) {
                triggerToast("You do not have permission to update raw material.", "error");
                return;
              }
              if (!editMaterial && !canWrite) {
                triggerToast("You do not have permission to add new raw material.", "error");
                return;
              }
              handleSubmit();
            }}
            disabled={loading || (editMaterial ? !canUpdate : !canWrite)}
            title={
              editMaterial
                ? (!canUpdate ? "No permission to update" : undefined)
                : (!canWrite ? "No permission to add" : undefined)
            }
          >
            {loading
              ? (editMaterial ? "Updating..." : "Saving...")
              : (editMaterial ? "Update" : "+ Add Item")}
          </button>
        </div>
        {showAddCategory && (
          <AddCategoryPopUp
            onClose={() => setShowAddCategory(false)}
            onSuccess={async (newCategoryId?: number) => {
              await fetchDropdowns();
              if (newCategoryId) setFormData(f => ({ ...f, category_id: String(newCategoryId) }));
              setShowAddCategory(false);
            }}
            parentCategories={categories}
          />
        )}
      </div>
    </div>
  );
};

export default AddRawMaterialPanel;
